import React, { useState, useEffect, useRef } from 'react';
import { Container, Row, Col, Card, Table, Badge, Button, Form, Spinner, Alert, Dropdown, ListGroup } from 'react-bootstrap';
import { Link, useNavigate } from 'react-router-dom';
import { agentApi, notificationApi, searchApi } from '../../services/api';
import { FaSearch, FaTimes, FaEye, FaDownload, FaFileInvoice, FaUser, FaSignOutAlt, FaBell, FaQuestionCircle } from 'react-icons/fa';
import AgentSidebar from '../../components/AgentSidebar';
import SearchResults from '../../components/SearchResults';
import Pagination from '../../components/Pagination';
import './Dashboard.css';
import { useAuth } from '../../hooks/useAuth';

// Helper function to format currency
const formatCurrency = (amount) => {
  if (amount === null || amount === undefined) return '₹0';
  return `₹${amount.toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
};

// Helper function to format dates
const formatDate = (dateString) => {
  if (!dateString) return '-';
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', { 
      day: '2-digit', 
      month: 'short', 
      year: 'numeric' 
    });
  } catch (e) {
    return dateString;
  }
};

const AgentInvoices = () => {
  const navigate = useNavigate();
  const { getCurrentUser, logout } = useAuth();
  const currentUser = getCurrentUser();
  
  // State for invoices data
  const [invoices, setInvoices] = useState([]);
  const [totalInvoices, setTotalInvoices] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  // State for pagination and filtering
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(10); // Fixed page size
  const [approvalStatusFilter, setApprovalStatusFilter] = useState('');
  const [paymentStatusFilter, setPaymentStatusFilter] = useState('');
  const [invoiceNumber, setInvoiceNumber] = useState('');
  
  // Notifications state
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loadingNotifications, setLoadingNotifications] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const notificationRef = useRef(null);
  
  // Search state
  const [searchQuery, setSearchQuery] = useState('');
  const [searchTimeout, setSearchTimeout] = useState(null);
  const [searchResults, setSearchResults] = useState([]);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const searchRef = useRef(null);
  
  // Calculate pagination parameters
  const skip = (currentPage - 1) * pageSize;
  const limit = pageSize;
  
  // Status options for filters
  const approvalStatusOptions = [
    { value: '', label: 'All Statuses' },
    { value: 'Pending', label: 'Pending' },
    { value: 'Approved', label: 'Approved' },
    { value: 'Rejected', label: 'Rejected' }
  ];
  
  const paymentStatusOptions = [
    { value: '', label: 'All Payment Statuses' },
    { value: 'Generated', label: 'Generated' },
    { value: 'Completed', label: 'Paid' },
    { value: 'Failed', label: 'Failed' }
  ];
  
  // Status badge mappers
  const getApprovalStatusBadge = (status) => {
    switch (status?.toLowerCase()) {
      case 'approved':
        return <Badge bg="success">Approved</Badge>;
      case 'rejected':
        return <Badge bg="danger">Rejected</Badge>;
      case 'pending':
      default:
        return <Badge bg="warning">Pending</Badge>;
    }
  };
  
  const getPaymentStatusBadge = (status) => {
    switch (status?.toLowerCase()) {
      case 'completed':
        return <Badge bg="success">Paid</Badge>;
      case 'failed':
        return <Badge bg="danger">Failed</Badge>;
      case 'generated':
      default:
        return <Badge bg="info">Generated</Badge>;
    }
  };
  
  // Handle logout
  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Search handlers
  const handleSearchChange = (e) => {
    const query = e.target.value;
    setSearchQuery(query);
    
    // Clear previous timeout
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }
    
    if (query.trim().length >= 2) {
      // Set a new timeout for search
      const timeout = setTimeout(() => {
        performSearch(query);
      }, 500); // Debounce 500ms
      setSearchTimeout(timeout);
    } else {
      setShowSearchResults(false);
      setSearchResults([]);
    }
  };

  const performSearch = async (query) => {
    if (!query.trim() || query.length < 2) return;
    
    setIsSearching(true);
    try {
      const response = await searchApi.globalSearch(query, { limit_per_type: 5 });
      setSearchResults(response.data.results || []);
      setShowSearchResults(true);
    } catch (error) {
      console.error('Error performing search:', error);
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };
  
  const clearSearch = () => {
    setSearchQuery('');
    setSearchResults([]);
    setShowSearchResults(false);
  };
  
  // Notification handlers
  const toggleNotifications = () => {
    setShowNotifications(prev => !prev);
  };
  
  const handleMarkAsRead = async (notificationId) => {
    const notification = notifications.find(n => n.id === notificationId);
    if (!notification || notification.is_read) return;

    try {
      await notificationApi.markAsRead(notificationId);
      setNotifications(prev => prev.map(n => n.id === notificationId ? { ...n, is_read: true } : n));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };
  
  const handleMarkAllAsRead = async () => {
    if (unreadCount === 0) return;

    try {
      await notificationApi.markAllAsRead();
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      setUnreadCount(0);
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
    }
  };
  
  const formatTimestamp = (timestamp) => {
    try {
      return new Date(timestamp).toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' });
    } catch (e) { return timestamp; }
  };
  
  // Fetch invoices data
  const fetchInvoices = async () => {
    setLoading(true);
    setError('');
    
    try {
      const response = await agentApi.getInvoices({
        skip,
        limit,
        approval_status: approvalStatusFilter,
        payment_status: paymentStatusFilter,
        invoice_number: invoiceNumber
      });
      
      setInvoices(response.data.items || []);
      setTotalInvoices(response.data.total || 0);
    } catch (err) {
      console.error('Error fetching invoices:', err);
      setError('Failed to load invoices. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  // Initial data load and when filters/pagination change
  useEffect(() => {
    fetchInvoices();
  }, [currentPage, approvalStatusFilter, paymentStatusFilter]); // Re-fetch when page or filters change
  
  // Notifications polling effect
  useEffect(() => {
    let intervalId;
    const fetchNotificationsData = async () => {
      try {
        const countResponse = await notificationApi.getUnreadCount();
        setUnreadCount(countResponse.data.unread_count);
      } catch (error) {
        console.error('Error fetching notifications count:', error);
      }
    };

    fetchNotificationsData(); // Initial fetch
    intervalId = setInterval(fetchNotificationsData, 60000); // Poll count every minute
    
    return () => clearInterval(intervalId); // Cleanup
  }, []);

  // Fetch full notifications when dropdown opens
  useEffect(() => {
    const fetchFullNotifications = async () => {
      if (showNotifications) {
        setLoadingNotifications(true);
        try {
          const notificationsResponse = await notificationApi.getNotifications({ limit: 10 });
          setNotifications(notificationsResponse.data);
        } catch (error) {
          console.error('Error fetching notifications:', error);
          setNotifications([]);
        } finally {
          setLoadingNotifications(false);
        }
      }
    };
    fetchFullNotifications();
  }, [showNotifications]);

  // Handle clicks outside notification dropdown and search results
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (notificationRef.current && !notificationRef.current.contains(event.target)) {
        setShowNotifications(false);
      }
      
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowSearchResults(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  
  // Handle search form submission
  const handleSearch = (e) => {
    e.preventDefault();
    setCurrentPage(1); // Reset to first page when searching
    fetchInvoices();
  };
  
  // Handle view invoice details
  const handleViewInvoice = (invoiceId) => {
    navigate(`/agent/invoices/${invoiceId}`);
  };
  
  // Handle download invoice
  const handleDownloadInvoice = (invoiceUrl) => {
    if (invoiceUrl) {
      window.open(invoiceUrl, '_blank');
    }
  };

  // Handle Create Invoice button click
  const handleCreateInvoiceClick = () => {
    navigate('/agent/create-invoice');
  };

  return (
    <div className="agent-dashboard-layout">
      <AgentSidebar />
      <div className="agent-content-wrapper">
        <header className="agent-header">
          <div className="agent-greeting">
            <span className="agent-greeting-text">Hi, {currentUser?.name || 'Agent'} <span className="agent-greeting-emoji">👋</span></span>
          </div>

          <div ref={searchRef} className="agent-header-search">
            <div className="search-input-container">
              <FaSearch className="search-icon" />
              <input 
                type="text" 
                placeholder="Search leads, products, invoices..."
                value={searchQuery}
                onChange={handleSearchChange}
                onFocus={() => {
                  if (searchResults.length > 0) {
                    setShowSearchResults(true);
                  }
                }}
              />
              {searchQuery && (
                <button className="clear-search-btn" onClick={clearSearch}>
                  <FaTimes />
                </button>
              )}
              {isSearching && (
                <Spinner 
                  animation="border" 
                  size="sm" 
                  className="search-spinner" 
                />
              )}
            </div>
            
            {/* Search results dropdown */}
            {showSearchResults && searchResults.length > 0 && (
              <div className="search-results-dropdown">
                <SearchResults 
                  results={searchResults}
                  onResultClick={() => setShowSearchResults(false)}
                />
              </div>
            )}
          </div>

          <div className="agent-header-actions">
            <div className="agent-header-icon">
              <FaQuestionCircle size={18} />
            </div>
            
            <div ref={notificationRef} className="position-relative agent-notifications me-3">
              <Button
                variant="link"
                className="text-secondary p-0 position-relative"
                onClick={toggleNotifications}
                id="dropdown-notifications-agent"
                aria-expanded={showNotifications}
                title="Notifications"
              >
                <FaBell size={20} />
                {unreadCount > 0 && (
                  <Badge
                    pill
                    bg="danger"
                    className="position-absolute top-0 start-100 translate-middle border border-light"
                    style={{ fontSize: '0.65em', padding: '0.3em 0.5em' }}
                  >
                    {unreadCount > 9 ? '9+' : unreadCount}
                    <span className="visually-hidden">unread notifications</span>
                  </Badge>
                )}
              </Button>

              {/* Notification Dropdown Menu */}
              <Dropdown.Menu
                show={showNotifications}
                className="shadow mt-2 notification-dropdown"
                style={{ minWidth: '350px', maxHeight: '450px', overflowY: 'auto' }}
              >
                <Dropdown.Header className="d-flex justify-content-between align-items-center">
                  Notifications
                  {unreadCount > 0 && (
                    <Button variant="link" size="sm" onClick={handleMarkAllAsRead} className="p-0 text-decoration-none">
                      Mark all read
                    </Button>
                  )}
                </Dropdown.Header>
                <Dropdown.Divider className="my-1" />
                {loadingNotifications ? (
                  <div className="text-center p-3">
                    <Spinner animation="border" size="sm" variant="primary" /> Loading...
                  </div>
                ) : notifications.length > 0 ? (
                  <ListGroup variant="flush">
                    {notifications.map(notif => (
                      <ListGroup.Item
                        key={notif.id}
                        action={!notif.is_read}
                        onClick={() => handleMarkAsRead(notif.id)}
                        style={{
                          cursor: !notif.is_read ? 'pointer' : 'default',
                          backgroundColor: !notif.is_read ? '#f8f9fa' : 'transparent',
                          borderLeft: !notif.is_read ? '4px solid #0d6efd' : 'none',
                          padding: '0.75rem 1.25rem',
                          borderBottom: '1px solid #eee'
                        }}
                        className="notification-item"
                      >
                        <div className={`fw-bold ${!notif.is_read ? 'text-primary' : ''}`}>{notif.title || 'Notification'}</div>
                        <div className="text-muted small mb-1">{notif.message || 'No details available.'}</div>
                        <small className="text-muted">{formatTimestamp(notif.created_at)}</small>
                      </ListGroup.Item>
                    ))}
                  </ListGroup>
                ) : (
                  <Dropdown.ItemText className="text-center text-muted p-3">
                    You're all caught up!
                  </Dropdown.ItemText>
                )}
              </Dropdown.Menu>
            </div>

            <div className="agent-user-profile">
              <div className="agent-avatar">
                <FaUser />
              </div>
              <div className="agent-user-info">
                <span className="agent-user-name">
                  {currentUser?.name || 'Agent User'}
                </span>
                <button className="agent-logout-btn" onClick={handleLogout}>
                  <FaSignOutAlt size={14} />
                  <span>Sign out</span>
                </button>
              </div>
            </div>
          </div>
        </header>

        <div className="agent-main-content">
          <Container fluid className="p-4">
            {/* Updated Header Row */}
            <Row className="mb-4 align-items-center">
              <Col xs={12} md={8}>
                <h2 className="page-title">My Invoices</h2>
                <p className="page-subtitle">View and manage all your invoices</p>
              </Col>
              <Col xs={12} md={4} className="text-md-end mt-3 mt-md-0">
                <Button variant="success" onClick={handleCreateInvoiceClick}>
                  <FaFileInvoice className="me-1" /> Create New Invoice
                </Button>
              </Col>
            </Row>

          <Card className="mb-4">
            <Card.Body>
              <Row className="mb-3">
                <Col md={6} lg={4}>
                  <Form onSubmit={handleSearch}>
                    <div className="d-flex">
                      <Form.Group className="flex-grow-1 me-2">
                        <div className="search-input-container">
                          <FaSearch className="search-icon" />
                          <Form.Control
                            type="text"
                            placeholder="Search by invoice number"
                            value={invoiceNumber}
                            onChange={(e) => setInvoiceNumber(e.target.value)}
                          />
                          {invoiceNumber && (
                            <Button 
                              variant="link" 
                              className="clear-search-btn"
                              onClick={() => {
                                setInvoiceNumber('');
                                if (invoiceNumber) {
                                  setCurrentPage(1);
                                  fetchInvoices();
                                }
                              }}
                            >
                              <FaTimes />
                            </Button>
                          )}
                        </div>
                      </Form.Group>
                      <Button type="submit" variant="primary">Search</Button>
                    </div>
                  </Form>
                </Col>
                <Col md={6} lg={8} className="d-flex justify-content-md-end mt-3 mt-md-0">
                  <Form.Group className="d-flex align-items-center me-3">
                    <Form.Label className="me-2 mb-0">Approval:</Form.Label>
                    <Form.Select
                      value={approvalStatusFilter}
                      onChange={(e) => {
                        setApprovalStatusFilter(e.target.value);
                        setCurrentPage(1);
                      }}
                      style={{ width: '130px' }}
                    >
                      {approvalStatusOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                  <Form.Group className="d-flex align-items-center">
                    <Form.Label className="me-2 mb-0">Payment:</Form.Label>
                    <Form.Select
                      value={paymentStatusFilter}
                      onChange={(e) => {
                        setPaymentStatusFilter(e.target.value);
                        setCurrentPage(1);
                      }}
                      style={{ width: '130px' }}
                    >
                      {paymentStatusOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>

              {loading ? (
                <div className="text-center p-5">
                  <Spinner animation="border" variant="primary" />
                  <p className="mt-3">Loading invoices...</p>
                </div>
              ) : error ? (
                <Alert variant="danger">{error}</Alert>
              ) : invoices.length > 0 ? (
                <>
                  <div className="table-responsive">
                    <Table hover className="custom-table">
                      <thead>
                        <tr>
                          <th>Invoice Number</th>
                          <th>Amount</th>
                          <th>Created Date</th>
                          <th>Approval Status</th>
                          <th>Payment Status</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {invoices.map((invoice) => (
                          <tr key={invoice.id}>
                            <td>{invoice.invoice_no}</td>
                            <td>{formatCurrency(invoice.amount)}</td>
                            <td>{formatDate(invoice.created_at)}</td>
                            <td>{getApprovalStatusBadge(invoice.invoice_status_str)}</td>
                            <td>{getPaymentStatusBadge(invoice.payment_status_str)}</td>
                            <td>
                              <Button
                                variant="outline-primary"
                                size="sm"
                                className="me-2"
                                onClick={() => handleViewInvoice(invoice.id)}
                              >
                                <FaEye className="me-1" /> View
                              </Button>
                              {invoice.invoice_url && (
                                <Button
                                  variant="outline-success"
                                  size="sm"
                                  onClick={() => handleDownloadInvoice(invoice.invoice_url)}
                                >
                                  <FaDownload className="me-1" /> Download
                                </Button>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                  <div className="d-flex justify-content-between align-items-center mt-4">
                    <div>
                      Showing <strong>{Math.min(skip + 1, totalInvoices)}</strong> to{" "}
                      <strong>{Math.min(skip + invoices.length, totalInvoices)}</strong> of{" "}
                      <strong>{totalInvoices}</strong> invoices
                    </div>
                    <Pagination
                      currentPage={currentPage}
                      totalPages={Math.ceil(totalInvoices / pageSize)}
                      onPageChange={setCurrentPage}
                    />
                  </div>
                </>
              ) : (
                <div className="text-center p-5">
                  <FaFileInvoice size={48} className="text-muted mb-3" />
                  <h5>No invoices found</h5>
                  <p className="text-muted">
                    Create invoices for your approved payouts to see them here.
                  </p>
                </div>
              )}
            </Card.Body>
          </Card>
        </Container>
      </div>
    </div>
    
    <style jsx>{`
        .page-title {
          font-size: 1.5rem;
          font-weight: 600;
          margin-bottom: 0.5rem;
          color: #1e293b;
        }
        
        .page-subtitle {
          color: #64748b;
          margin-bottom: 0;
        }
        
        .search-input-container {
          position: relative;
        }
        
        .search-icon {
          position: absolute;
          left: 10px;
          top: 50%;
          transform: translateY(-50%);
          color: #64748b;
        }
        
        .form-control {
          padding-left: 35px;
        }
        
        .clear-search-btn {
          position: absolute;
          right: 10px;
          top: 50%;
          transform: translateY(-50%);
          padding: 0;
          color: #64748b;
        }
        
        .custom-table thead th {
          background-color: #f8fafc;
          color: #64748b;
          font-weight: 600;
          font-size: 0.875rem;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          border-bottom: 1px solid #e2e8f0;
          padding: 0.75rem 1rem;
        }
        
        .custom-table tbody td {
          padding: 1rem;
          vertical-align: middle;
          border-bottom: 1px solid #e2e8f0;
          color: #334155;
          font-size: 0.9375rem;
        }
        
        .custom-table tbody tr:hover {
          background-color: #f1f5f9;
        }
        
        @media (max-width: 768px) {
          .form-group {
            margin-bottom: 1rem;
          }
        }
      `}</style>
    </div>
  );
};

export default AgentInvoices;
